import { useState, useEffect, useRef } from 'react';
import { Project, Equipment, AMIDataPoint } from '../types/project';
import { calculateNEC22087, CalculationResults } from '../lib/calculations';
import { AlertCircle, CheckCircle, CheckSquare, FileText, X, Printer, Mail, Share2 } from 'lucide-react';
import { Tooltip } from './Tooltip';
import { formatReportData, generateHTMLReport } from '../utils/reportGenerator';
import { generateReportPDF } from '../utils/generatePDF';
import { PaymentStatus } from '../hooks/usePaymentStatus';
import { ResultsLockScreen } from './ResultsLockScreen';
import { showsUnclassifiedLoadNotice } from '../lib/concurrency';
import { enteredProposedLoads, enteredRemovedLoads } from '../lib/calc/loadRoles';
import { citationsFor, measuredMethodologyVersion, traditionalMethodologyVersion } from '../lib/calc/citations';
import { canRunTraditional, runTraditionalCalc } from '../lib/calc/runTraditional';
import { canRunMeasured, effectiveReportMethod, reportMethodCorrection } from '../lib/calc/reportBasis';
import {
  basisReason,
  compareMethods,
  connectedLoadVA,
  inventoryCheck,
  zeroRatedRows,
} from '../lib/calc/comparison';
import { kvaToAmps } from '../lib/calc/amps';
import { getEditionPack, resolvedEditionKey } from '../lib/calc/editions';
import { calcRulesFor } from '../lib/calc/rulesVersion';
import { TraditionalCalcResults } from '../lib/calc/types';
import { TraditionalResultsSummary } from './TraditionalResultsSummary';
import { traditionalQualityIssues } from '../lib/profileQuality';
import CompletionValidationModal from './CompletionValidationModal';
import {
  validateProjectForCompletion,
  type ValidationResult,
} from '../validation/projectCompletionValidation';
import { supabase } from '../lib/supabase';
import { isSubscribed as isSubscribedTo } from '../lib/paywall';

interface ResultsViewProps {
  project: Partial<Project>;
  equipment: Equipment[];
  amiData: AMIDataPoint[];
  projectId: string | null;
  paymentStatus: PaymentStatus;
  onMarkComplete?: () => void;
  onChange?: (updates: Partial<Project>) => void;
  onPaymentStatusRefresh: () => void;
  /** Jumps to the tab owning a failed check. Absent on read-only surfaces. */
  onNavigate?: (tab: 'project' | 'service' | 'equipment' | 'ami') => void;
  onEnsureSaved?: () => Promise<string | null>;
  /** Firm logo for the report letterhead — see ReportData.firmLogoSrc. */
  firmLogoSrc?: string | null;
}

export function ResultsView({
  project,
  equipment: allEquipment,
  amiData,
  projectId,
  paymentStatus,
  onMarkComplete,
  onChange,
  onPaymentStatusRefresh,
  onEnsureSaved,
  onNavigate,
  firmLogoSrc = null,
}: ResultsViewProps) {
  // The 220.87 result, its report, and completion validation all run on
  // proposed loads only — dwelling-inventory rows (load_role 'existing') are
  // traditional-path inputs, not new loads.
  const equipment = enteredProposedLoads(allEquipment);
  // Equipment taken out with this work: listed on every measured result,
  // credited only under 2026 (measured22087.ts removedLoadCreditApplies).
  const removed = enteredRemovedLoads(allEquipment);
  // The measured method's section number follows the project's code edition:
  // 220.87 under 2020/2023, 120.87 where the 2026 Article 120 reorg is
  // enforced (Massachusetts since April 2026). No literal below this line.
  const measuredCite = citationsFor(project).measured;
  const [unlockToast, setUnlockToast] = useState<string | null>(null);
  // Opt-in per generation, deliberately not persisted: the permit document
  // carries the basis method alone unless the preparer chooses otherwise.
  const [includeComparison, setIncludeComparison] = useState(false);
  // What-if service size for the comparison line; display-only, never saved.
  const [showReportModal, setShowReportModal] = useState(false);
  const [showReportValidationModal, setShowReportValidationModal] = useState(false);
  const [reportValidation, setReportValidation] = useState<ValidationResult | null>(null);
  const [reportBlobUrl, setReportBlobUrl] = useState<string | null>(null);
  const [reportPdfBlob, setReportPdfBlob] = useState<Blob | null>(null);
  const [generatingPdf, setGeneratingPdf] = useState(false);
  const iframeRef = useRef<HTMLIFrameElement>(null);
  const [shareNotSupported, setShareNotSupported] = useState(false);

  // Which existing-load paths can produce a result right now.
  const measuredReady = canRunMeasured(project, amiData);
  // Inputs present AND inside the section's scope — an ineligible project is
  // a measured-path project here, never a traditional one.
  const traditionalReady = canRunTraditional(project);

  const hasData =
    amiData.length > 0 ||
    (project.max_recorded_demand_kw != null && project.max_recorded_demand_kw > 0) ||
    traditionalReady;

  // A firm subscription covers the people holding its paid SEATS, not everyone
  // whose firm_id happens to be set. A firm admin who runs no projects is free
  // and holds no seat; until 2026-08-03 they were unlocked anyway, which is the
  // revenue leak seat enforcement exists to close.
  const isSubscribed = isSubscribedTo(paymentStatus);
  const isUnlocked = project.results_unlocked === true || isSubscribed;

  // Fire-and-forget: when a subscribed user views Results on an as-yet-unlocked project,
  // persist results_unlocked = true so the DB reflects the grant.
  useEffect(() => {
    if (
      isSubscribed &&
      !project.results_unlocked &&
      projectId &&
      !projectId.startsWith('local_') &&
      hasData
    ) {
      const now = new Date().toISOString();
      supabase
        .from('projects')
        .update({ results_unlocked: true, results_unlocked_at: now })
        .eq('id', projectId)
        .then(() => {});
      onChange?.({ results_unlocked: true, results_unlocked_at: now });
    }
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isSubscribed, project.results_unlocked, projectId]);

  // When exactly one path can produce a result, record it — validation,
  // progress, and pass_fail all key off report_method. That includes
  // replacing a stored basis whose path cannot run (reportBasis.ts). When
  // both paths are live the picker below makes it an explicit choice.
  useEffect(() => {
    if (!onChange) return;
    const m = reportMethodCorrection(project, amiData);
    if (!m) return;
    onChange({
      report_method: m,
      methodology_version:
        m === 'measured' ? measuredMethodologyVersion(project) : traditionalMethodologyVersion(project),
    });
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [measuredReady, traditionalReady, project.report_method]);

  useEffect(() => {
    if (!showReportModal) {
      if (reportBlobUrl) {
        URL.revokeObjectURL(reportBlobUrl);
        setReportBlobUrl(null);
      }
      setReportPdfBlob(null);
    }
  }, [showReportModal]);

  useEffect(() => {
    if (!showReportModal) return;
    const handler = (e: KeyboardEvent) => {
      if (e.key === 'Escape') setShowReportModal(false);
    };
    document.addEventListener('keydown', handler);
    return () => document.removeEventListener('keydown', handler);
  }, [showReportModal]);

  function handleUnlockSuccess() {
    const now = new Date().toISOString();
    onChange?.({ results_unlocked: true, results_unlocked_at: now });
    onPaymentStatusRefresh();
    setUnlockToast('Project unlocked. Results are permanently available.');
    setTimeout(() => setUnlockToast(null), 5000);
  }

  // Paywall loading state
  if (paymentStatus.loading) {
    return (
      <div className="flex items-center justify-center py-20">
        <div className="animate-spin rounded-full h-8 w-8 border-2 border-blue-600 border-t-transparent" />
      </div>
    );
  }

  // Lock screen — no data or not paid
  if (!isUnlocked) {
    return (
      <div className="space-y-4">
        <ResultsLockScreen
          project={project}
          amiData={amiData}
          equipment={allEquipment}
          projectId={projectId}
          paymentStatus={paymentStatus}
          onUnlockSuccess={handleUnlockSuccess}
          onEnsureSaved={onEnsureSaved}
        />
      </div>
    );
  }

  // Readiness gate — unlocked but neither path can produce a result yet.
  if (!measuredReady && !traditionalReady) {
    return (
      <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-6">
        <div className="flex items-center gap-3 p-4 bg-amber-50 border border-amber-200 rounded-lg">
          <AlertCircle className="w-6 h-6 text-amber-600 flex-shrink-0" />
          <div>
            <p className="font-semibold text-amber-900 text-sm">Existing Load Not Established</p>
            <p className="text-xs text-amber-700 mt-1">
              Establish the existing load on the Existing Load tab — upload interval data
              (Measured demand, NEC {measuredCite}), or fill in the dwelling profile (Traditional
              calculation). Either path produces results here.
            </p>
          </div>
        </div>
      </div>
    );
  }

  const measuredResults: CalculationResults | null = measuredReady
    ? calculateNEC22087(project as Project, equipment, amiData, removed)
    : null;
  const traditionalResults: TraditionalCalcResults | null = traditionalReady
    ? runTraditionalCalc(project, allEquipment)
    : null;

  // The report basis: the stored choice when its path is live, else whichever
  // path is. Measured wins an unstored tie — it is the product's headline.
  const basis = effectiveReportMethod(project, amiData);

  // Always materialized so ReportData.results stays satisfied; rendered only
  // under a measured basis. With no measured data it is the zero calc.
  const results: CalculationResults =
    measuredResults ?? calculateNEC22087(project as Project, equipment, amiData, removed);
  const isPassing =
    basis === 'measured'
      ? measuredResults!.adequacyResult === 'PASS'
      : traditionalResults!.adequacyResult === 'PASS';
  const basisCitation =
    basis === 'measured' ? measuredCite : traditionalResults!.sectionCitation;

  const chooseBasis = (m: 'measured' | 'traditional') => {
    onChange?.({
      report_method: m,
      methodology_version:
        m === 'measured' ? measuredMethodologyVersion(project) : traditionalMethodologyVersion(project),
    });
  };

  const canShare =
    typeof navigator.share === 'function' &&
    typeof navigator.canShare === 'function';

  /**
   * Step 1 of report generation: run the same checklist that gates Mark Complete.
   *
   * The report is the deliverable, so it is what the required-field rules have to
   * guard — completion is bookkeeping. Nothing is withheld: an unfinished project
   * still generates, stamped DRAFT — INCOMPLETE, so previewing mid-job stays
   * possible without an incomplete document passing as a finished one.
   */
  async function handleInitiateReport() {
    // Full equipment list — the validator is report-basis-aware and splits
    // proposed rows from dwelling-inventory rows itself.
    const result = await validateProjectForCompletion(
      projectId ?? '',
      project,
      allEquipment,
      amiData
    );
    setReportValidation(result);
    setShowReportValidationModal(true);
  }

  /** Step 2: called by the modal once the user has seen the checklist. */
  async function handleOpenReport() {
    setShowReportValidationModal(false);
    const reportData = {
      project: project as Project,
      // Full list — the traditional formatter needs the dwelling inventory;
      // the measured formatter only looks up rows the calc already selected.
      equipment: allEquipment,
      amiData,
      results,
      reportDate: new Date().toISOString(),
      isDraft: (reportValidation?.summary.blockerCount ?? 0) > 0,
      method: basis,
      traditionalResults: traditionalResults ?? undefined,
      // True only when the measured method genuinely ran — gates the Methods
      // Computed block together with the explicit opt-in below.
      measuredRan: measuredReady,
      includeMethodsComparison: includeComparison,
      firmLogoSrc,
    };
    const data = formatReportData(reportData);
    const html = generateHTMLReport(data);

    // Generate HTML blob for iframe preview
    const htmlBlob = new Blob([html], { type: 'text/html' });
    const url = URL.createObjectURL(htmlBlob);
    setReportBlobUrl(url);
    setShowReportModal(true);

    // Generate PDF blob for download and share
    setGeneratingPdf(true);
    try {
      const pdfBlob = await generateReportPDF(html);
      setReportPdfBlob(pdfBlob);
    } finally {
      setGeneratingPdf(false);
    }
  }

  function handleCloseModal() {
    setShowReportModal(false);
  }

  function handlePrint() {
    if (iframeRef.current?.contentWindow) {
      iframeRef.current.contentWindow.focus();
      iframeRef.current.contentWindow.print();
    }
  }

  function handleDownloadAndEmail() {
    const blob = reportPdfBlob;
    if (!blob) return;
    const location = project.customer_address || project.project_name || 'Report';
    const filename = `TrueLoad-Report-${location.replace(/[^a-z0-9]/gi, '-')}.pdf`;

    const a = document.createElement('a');
    a.href = URL.createObjectURL(blob);
    a.download = filename;
    document.body.appendChild(a);
    a.click();
    document.body.removeChild(a);

    const loc = project.customer_address || project.project_name || 'the property';
    setTimeout(() => {
      const subject = encodeURIComponent(`TrueLoad Load Analysis Report — ${loc}`);
      const body = encodeURIComponent(`Please find the attached TrueLoad NEC ${basisCitation} load analysis report for ${loc}. Attach the PDF you just downloaded to this email. Generated by TrueLoad (trueloadapp.com).`);
      window.location.href = `mailto:?subject=${subject}&body=${body}`;
    }, 300);
  }

  async function handleShare() {
    const blob = reportPdfBlob;
    if (!blob) return;
    try {
      const location = project.customer_address || project.project_name || 'Report';
      const filename = `TrueLoad-Report-${location.replace(/[^a-z0-9]/gi, '-')}.pdf`;
      const file = new File([blob], filename, { type: 'application/pdf' });

      if (navigator.canShare && navigator.canShare({ files: [file] })) {
        await navigator.share({
          title: 'TrueLoad Load Analysis Report',
          text: `NEC ${basisCitation} load analysis report for ${location}`,
          files: [file],
        });
      } else {
        setShareNotSupported(true);
        setTimeout(() => setShareNotSupported(false), 3000);
      }
    } catch {
      // User cancelled — fail silently
    }
  }

  return (
    <div className="space-y-6">
      <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-6">
        <div className="flex items-center justify-between mb-6">
          <h2 className="text-xl font-semibold text-gray-900">
            Service Adequacy Evaluation Results
          </h2>
          <div className="flex items-center gap-2">
            <button
              data-testid="btn-generate-report"
              onClick={handleInitiateReport}
              disabled={generatingPdf}
              className="flex items-center space-x-2 px-4 py-2 bg-blue-600 text-white rounded-md hover:bg-blue-700 transition-colors disabled:bg-blue-400 disabled:cursor-not-allowed"
            >
              {generatingPdf ? (
                <>
                  <div className="animate-spin rounded-full h-5 w-5 border-2 border-white border-t-transparent" />
                  <span>Generating...</span>
                </>
              ) : (
                <>
                  <FileText className="w-5 h-5" />
                  <span>Generate Report</span>
                </>
              )}
            </button>
            {onMarkComplete && project.status !== 'completed' && (
              <>
                <button
                  onClick={onMarkComplete}
                  className="flex items-center space-x-2 px-4 py-2 bg-green-600 text-white rounded-md hover:bg-green-700 transition-colors"
                >
                  <CheckSquare className="w-5 h-5" />
                  <span>Mark Complete</span>
                </button>
                <Tooltip text="Locks the project as read-only and marks it ready for reporting. You can unlock it later if changes are needed." />
              </>
            )}
            {project.status === 'completed' && (
              <div className="flex items-center space-x-2 px-4 py-2 bg-green-100 text-green-800 rounded-md border border-green-300">
                <CheckCircle className="w-5 h-5" />
                <span className="font-medium">Project Completed</span>
              </div>
            )}
          </div>
        </div>

        {measuredResults && traditionalResults && (
          <label className="mb-4 flex items-center gap-2 text-xs text-gray-600 cursor-pointer">
            <input
              type="checkbox"
              checked={includeComparison}
              onChange={(e) => setIncludeComparison(e.target.checked)}
              data-testid="include-methods-comparison"
              className="w-4 h-4 text-blue-600 rounded focus:ring-2 focus:ring-blue-500"
            />
            Include the methods comparison on the generated report
            <Tooltip text="Off by default — the permit document carries the basis method alone. Include it when the method choice itself is being questioned: the report then shows every method that ran (including any that fail), the stated reason for the basis, and the NEC 90.5(B) note." />
          </label>
        )}

        {measuredResults && traditionalResults && (() => {
          const cmp = compareMethods(
            measuredResults.adjustedDemandKVA,
            traditionalResults.totalDemandKVA
          );
          const connectedKVA = connectedLoadVA(traditionalResults.lineItems) / 1000;
          const inv = inventoryCheck(measuredResults.maxRecordedDemandKVA, connectedKVA);
          const zeros = zeroRatedRows(traditionalResults.lineItems);
          const chip = (result: 'PASS' | 'FAIL') => (
            <span
              className={`inline-block px-2 py-0.5 rounded text-xs font-semibold ${
                result === 'PASS' ? 'bg-green-100 text-green-800' : 'bg-red-100 text-red-800'
              }`}
            >
              {result}
            </span>
          );
          const basisButton = (m: 'measured' | 'traditional', testid: string) => (
            <button
              onClick={() => chooseBasis(m)}
              disabled={!onChange}
              data-testid={testid}
              className={`px-3 py-1.5 rounded-md text-xs font-medium border transition-colors whitespace-nowrap ${
                basis === m
                  ? 'bg-blue-600 text-white border-blue-600'
                  : 'bg-white text-gray-700 border-gray-300 hover:bg-gray-100'
              }`}
            >
              {basis === m ? 'Basis of record' : 'Set as basis'}
            </button>
          );
          return (
            <div
              data-testid="method-comparison"
              className="mb-6 border border-gray-200 rounded-lg bg-gray-50 overflow-hidden"
            >
              <div className="px-4 py-3 border-b border-gray-200">
                <p className="text-sm font-semibold text-gray-900">
                  Methods computed — choose the report basis
                </p>
                <p className="text-xs text-gray-600 mt-0.5">
                  Every method that ran is shown, including any that fail. The report prints
                  the basis worksheet in full with the others summarized alongside.
                </p>
              </div>
              <div className="overflow-x-auto">
                <table className="w-full text-xs sm:text-sm">
                  <thead>
                    <tr className="text-left text-gray-500">
                      <th className="px-4 py-2 font-medium">Method</th>
                      <th className="px-4 py-2 font-medium text-right whitespace-nowrap">
                        Calculated demand
                      </th>
                      <th className="px-4 py-2 font-medium text-right">Margin</th>
                      <th className="px-4 py-2 font-medium">Result</th>
                      <th className="px-4 py-2"></th>
                    </tr>
                  </thead>
                  <tbody className="divide-y divide-gray-200 bg-white">
                    <tr className={basis === 'measured' ? 'bg-blue-50' : ''}>
                      <td className="px-4 py-2 text-gray-900">Measured demand — NEC {measuredCite}</td>
                      <td className="px-4 py-2 text-right font-medium whitespace-nowrap">
                        {measuredResults.adjustedDemandKVA.toFixed(2)} kVA ·{' '}
                        {kvaToAmps(
                          measuredResults.adjustedDemandKVA,
                          project.service_voltage_numeric ?? 240,
                          project.service_phase === 'three' ? 'three' : 'single'
                        ).toFixed(1)}{' '}
                        A
                      </td>
                      <td className="px-4 py-2 text-right whitespace-nowrap">
                        {measuredResults.availableMarginKVA.toFixed(2)} kVA
                      </td>
                      <td className="px-4 py-2">{chip(measuredResults.adequacyResult)}</td>
                      <td className="px-4 py-2 text-right">
                        {basisButton('measured', 'btn-basis-measured')}
                      </td>
                    </tr>
                    <tr className={basis === 'traditional' ? 'bg-blue-50' : ''}>
                      <td className="px-4 py-2 text-gray-900">
                        Traditional calculation — NEC {traditionalResults.sectionCitation}
                      </td>
                      <td className="px-4 py-2 text-right font-medium whitespace-nowrap">
                        {traditionalResults.totalDemandKVA.toFixed(2)} kVA ·{' '}
                        {kvaToAmps(
                          traditionalResults.totalDemandKVA,
                          project.service_voltage_numeric ?? 240,
                          project.service_phase === 'three' ? 'three' : 'single'
                        ).toFixed(1)}{' '}
                        A
                      </td>
                      <td className="px-4 py-2 text-right whitespace-nowrap">
                        {traditionalResults.availableMarginKVA.toFixed(2)} kVA
                      </td>
                      <td className="px-4 py-2">{chip(traditionalResults.adequacyResult)}</td>
                      <td className="px-4 py-2 text-right">
                        {basisButton('traditional', 'btn-basis-traditional')}
                      </td>
                    </tr>
                  </tbody>
                </table>
              </div>
              <div className="px-4 py-3 border-t border-gray-200 space-y-1.5 text-xs">
                {cmp.kind === 'measured-lower' && (
                  <p data-testid="cmp-measured-lower" className="text-gray-700">
                    Measured demand calculates <strong>{cmp.deltaKVA.toFixed(2)} kVA lower</strong>{' '}
                    than the traditional method for this home.
                  </p>
                )}
                {cmp.kind === 'equal' && (
                  <p data-testid="cmp-equal" className="text-gray-700">
                    Both methods calculate {measuredResults.adjustedDemandKVA.toFixed(2)} kVA for
                    this home.
                  </p>
                )}
                {cmp.kind === 'traditional-lower' && (
                  <div
                    data-testid="cmp-traditional-lower"
                    className={inv?.band === 'implausible' ? 'text-amber-900' : 'text-gray-700'}
                  >
                    <p>
                      The traditional calculation is{' '}
                      <strong>{cmp.deltaKVA.toFixed(2)} kVA lower</strong> than the measured
                      basis — uncommon.
                    </p>
                    {inv?.band === 'implausible' ? (
                      <p className="mt-1">
                        The meter shows this house actually peaking at{' '}
                        {measuredResults.maxRecordedDemandKVA.toFixed(2)} kVA against only{' '}
                        {connectedKVA.toFixed(2)} kVA of entered connected load — an inventory
                        that small cannot produce that peak. Loads are almost certainly missing
                        from the Dwelling Profile.
                      </p>
                    ) : (
                      <p className="mt-1">
                        Verify the Dwelling Profile captures every fastened-in-place load before
                        relying on the traditional number.
                      </p>
                    )}
                  </div>
                )}
                {inv && (
                  <p data-testid="inventory-cross-check" className="text-gray-600">
                    Inventory cross-check: metered peak = <strong>{inv.percent}%</strong> of the
                    entered connected load ({connectedKVA.toFixed(2)} kVA).{' '}
                    {inv.band === 'normal' && (
                      <span className="text-green-700 font-medium">
                        Consistent — 20–35% is typical.
                      </span>
                    )}
                    {inv.band === 'low' && (
                      <span className="text-gray-500">
                        Below the typical 20–35%: a large inventory relative to the peak, which is
                        conservative.
                      </span>
                    )}
                    {inv.band === 'elevated' && (
                      <span className="text-amber-700 font-medium">
                        Above the typical 20–35% — review the inventory for missing loads.
                      </span>
                    )}
                    {inv.band === 'implausible' && (
                      <span className="text-red-700 font-semibold">
                        Above ~60% — the inventory is almost certainly incomplete.
                      </span>
                    )}
                  </p>
                )}
                {zeros.length > 0 && (
                  <p data-testid="cmp-zero-rated" className="text-amber-800 font-medium">
                    {zeros.length} inventory item(s) count at 0 VA — the traditional number is
                    understated until nameplate ratings are entered.
                  </p>
                )}
                <p className="text-gray-500 italic">
                  {basisReason(basis, traditionalResults.sectionCitation, measuredCite)}
                </p>
              </div>
            </div>
          );
        })()}

        {basis === 'measured' && showsUnclassifiedLoadNotice(project.created_at, equipment) && (
          <div
            data-testid="unclassified-load-notice"
            className="mb-6 flex items-start gap-3 rounded-lg border border-blue-200 bg-blue-50 p-4"
          >
            <AlertCircle className="w-5 h-5 text-blue-600 flex-shrink-0 mt-0.5" />
            <div className="text-sm text-blue-900">
              <p className="font-semibold">This result may differ from what you saw previously.</p>
              <p className="mt-1 leading-relaxed">
                Equipment typed as <strong>Other</strong> is now counted toward the peak by
                default. Previously it was left out, which understated the proposed load. Nothing
                on this project changed — only how an unclassified load is treated.
              </p>
              <p className="mt-1 leading-relaxed">
                If the appliance genuinely will not be running during the peak, you can exclude it
                on the New Loads tab by unticking <em>Concurrent with peak demand</em> and
                recording why.
              </p>
            </div>
          </div>
        )}

        {basis === 'traditional' && traditionalResults && (
          <TraditionalResultsSummary
            results={traditionalResults}
            project={project}
            profileIssues={traditionalQualityIssues(project, allEquipment)}
          />
        )}

        {basis === 'measured' && (
        <>
        <div
          className={`p-6 rounded-lg border-2 mb-6 ${
            isPassing
              ? 'bg-green-50 border-green-500'
              : 'bg-red-50 border-red-500'
          }`}
        >
          <div className="flex items-center gap-3">
            {isPassing ? (
              <CheckCircle className="text-green-600 flex-shrink-0" size={32} />
            ) : (
              <AlertCircle className="text-red-600 flex-shrink-0" size={32} />
            )}
            <div>
              <h3
                data-testid="result-pass-fail"
                className={`text-2xl font-bold ${
                  isPassing ? 'text-green-900' : 'text-red-900'
                }`}
              >
                Service Adequacy: {results.adequacyResult}
              </h3>
              <p
                className={`text-sm mt-1 ${
                  isPassing ? 'text-green-800' : 'text-red-800'
                }`}
              >
                {isPassing
                  ? `The existing service has adequate capacity for the proposed new loads under NEC ${measuredCite}.`
                  : `The existing service does NOT have adequate capacity for the proposed new loads under NEC ${measuredCite}. Service upgrade required.`}
              </p>
            </div>
          </div>
        </div>

        <div className="grid grid-cols-1 md:grid-cols-2 gap-6 mb-6">
          <div className="p-4 bg-gray-50 rounded-lg border border-gray-200">
            <h4 className="font-semibold text-gray-900 mb-3">
              Existing Demand
            </h4>
            <div className="space-y-2 text-sm">
              <div className="flex justify-between">
                <span className="text-gray-600">Max Recorded Demand:</span>
                <span data-testid="result-peak-demand" className="font-medium">
                  {results.maxRecordedDemandKW.toFixed(2)} kW
                </span>
              </div>
              <div className="flex justify-between">
                <span className="text-gray-600">Max Demand (kVA):</span>
                <span className="font-medium">
                  {results.maxRecordedDemandKVA.toFixed(2)} kVA
                </span>
              </div>
              <div className="flex justify-between">
                <span className="text-gray-600">Peak Season:</span>
                <span className="font-medium">{results.peakSeason}</span>
              </div>
              {results.peakTimestamp && (
                <div className="flex justify-between">
                  <span className="text-gray-600">Peak Time:</span>
                  <span className="font-medium">
                    {new Date(results.peakTimestamp).toLocaleString(undefined, { timeZone: 'UTC' })}
                  </span>
                </div>
              )}
            </div>
          </div>

          <div className="p-4 bg-gray-50 rounded-lg border border-gray-200">
            <h4 className="font-semibold text-gray-900 mb-3">
              Service Capacity
            </h4>
            <div className="space-y-2 text-sm">
              <div className="flex justify-between">
                <span className="text-gray-600">Service Rating:</span>
                <span className="font-medium">
                  {project.service_rating_amps} A @ {project.service_voltage_numeric}V
                </span>
              </div>
              <div className="flex justify-between">
                <span className="text-gray-600">Service Capacity:</span>
                <span data-testid="result-service-capacity" className="font-medium">
                  {results.serviceCapacityKVA.toFixed(2)} kVA
                </span>
              </div>
              <div className="flex justify-between">
                <span className="inline-flex items-center text-gray-600">
                  Utilization: <Tooltip text="Percentage of total service capacity consumed by the adjusted demand." />
                </span>
                <span data-testid="result-utilization" className="font-medium">
                  {results.utilizationPercent.toFixed(1)}%
                </span>
              </div>
            </div>
          </div>
        </div>

        <div className="border-t border-gray-200 pt-6">
          <h4 className="font-semibold text-gray-900 mb-4 text-sm sm:text-base">
            NEC {measuredCite} Load Calculation Summary
          </h4>
          {/* 2026 only, and only for a project calculating under rules
              revision 2+: the preparer's switch for an AHJ that reads 120.87
              Condition (2) as leaving new load at 100%. The full-rating result
              shows either way so the choice is made with both numbers in view. */}
          {getEditionPack(resolvedEditionKey(project)).new83Percentages && calcRulesFor(project) >= 2 && (
            <div data-testid="new-load-factors-election" className="mb-4 rounded-md border border-gray-200 bg-gray-50 p-3 text-xs text-gray-700">
              <label className="flex items-start gap-2 cursor-pointer">
                <input
                  type="checkbox"
                  checked={project.measured_new_load_factors !== false}
                  disabled={!onChange || project.status === 'completed'}
                  onChange={(e) => onChange?.({ measured_new_load_factors: e.target.checked })}
                  data-testid="new-load-factors-toggle"
                  className="mt-0.5 w-4 h-4 text-blue-600 rounded focus:ring-2 focus:ring-blue-500"
                />
                <span>
                  Apply the Table 120.83 demand factors to new load (NEC 2026)
                  <Tooltip text={`The 2026 NEC ${measuredCite} Condition (2) determines new load under Article 120 Parts I, III and IV. TrueLoad reads that as allowing Table 120.83's percentages: new EV chargers and central electric resistance heat at 80%, everything else at 50%. Turn this off if your inspector requires new load at 100%. The report states the reading and prints the 100% result either way.`} />
                </span>
              </label>
              <p className="mt-1 ml-6 text-gray-600">
                At 100% of rating: {results.adjustedDemandAt100KVA.toFixed(2)} kVA, {results.adequacyAt100}.
              </p>
            </div>
          )}
          <div className="overflow-x-auto -mx-6 sm:mx-0">
          <table className="w-full text-xs sm:text-sm min-w-[400px]">
            <tbody className="divide-y divide-gray-200">
              <tr>
                <td className="py-2 px-2 sm:px-0 text-gray-600">
                  Existing Demand
                  <Tooltip text="The measured peak demand from your AMI data, in kW." />
                </td>
                <td className="py-2 px-2 sm:px-0 text-right font-medium whitespace-nowrap">
                  {results.existingDemandKVA.toFixed(2)} kVA
                </td>
              </tr>
              <tr>
                <td className="py-2 px-2 sm:px-0 text-gray-600">
                  125% of Existing Demand (NEC {measuredCite})
                  <Tooltip text={`NEC ${measuredCite} requires existing demand to be multiplied by 1.25 as a built-in safety factor before adding new loads.`} />
                </td>
                <td data-testid="result-existing-demand" className="py-2 px-2 sm:px-0 text-right font-medium whitespace-nowrap">
                  {results.existingDemand125KVA.toFixed(2)} kVA
                </td>
              </tr>
              <tr>
                <td className="py-2 px-2 sm:px-0 text-gray-600">
                  New Concurrent Load (Added)
                  <Tooltip text={results.newLoadFactorsApplied
                    ? `Proposed equipment concurrent with the measured peak season, after the Table 120.83 demand factors. Connected: ${results.totalConcurrentNewLoadKVA.toFixed(2)} kVA.`
                    : 'Total demand of proposed equipment marked as concurrent with the measured peak season.'} />
                </td>
                <td data-testid="result-new-load" className="py-2 px-2 sm:px-0 text-right font-medium whitespace-nowrap">
                  {results.totalConcurrentNewLoadDemandKVA.toFixed(2)} kVA
                </td>
              </tr>
              {results.removedLoadCreditApplied && results.removedCalculations.length > 0 && (
                <tr>
                  <td className="py-2 px-2 sm:px-0 text-gray-600">
                    Removed Load Credit
                    <Tooltip text={`NEC ${measuredCite} (2026) subtracts removed load after the 125%. Each removed item running in the peak season is credited at 40% of its rating, the Table 120.83 rate for existing load; items that don't run in that season are credited at zero.`} />
                  </td>
                  <td data-testid="result-removed-credit" className="py-2 px-2 sm:px-0 text-right font-medium whitespace-nowrap">
                    −{results.totalRemovedCreditKVA.toFixed(2)} kVA
                  </td>
                </tr>
              )}
              <tr className="bg-blue-50 font-semibold">
                <td className="py-2 px-2 sm:px-0 text-gray-900">
                  Adjusted Demand
                  <Tooltip text="The total load the service must support — 125% of existing demand plus all concurrent new equipment." />
                </td>
                <td data-testid="result-adjusted-demand" className="py-2 px-2 sm:px-0 text-right whitespace-nowrap">
                  {results.adjustedDemandKVA.toFixed(2)} kVA ·{' '}
                  {kvaToAmps(
                    results.adjustedDemandKVA,
                    project.service_voltage_numeric ?? 240,
                    project.service_phase === 'three' ? 'three' : 'single'
                  ).toFixed(1)}{' '}
                  A
                </td>
              </tr>
              <tr className="bg-gray-50 font-semibold">
                <td className="py-2 px-2 sm:px-0 text-gray-900">
                  Service Capacity
                  <Tooltip text="The total capacity of the existing service in kVA, calculated as service rating (amps) × voltage ÷ 1,000." />
                </td>
                <td data-testid="result-service-capacity-detail" className="py-2 px-2 sm:px-0 text-right whitespace-nowrap">
                  {results.serviceCapacityKVA.toFixed(2)} kVA
                </td>
              </tr>
              <tr
                className={`font-bold ${
                  isPassing ? 'bg-green-100' : 'bg-red-100'
                }`}
              >
                <td className="py-2 px-2 sm:px-0 text-gray-900">
                  Available Margin
                  <Tooltip text="Positive value = PASS. Negative value = FAIL. Represents remaining service capacity after accounting for all loads." />
                </td>
                <td
                  data-testid="result-margin"
                  className={`py-2 px-2 sm:px-0 text-right whitespace-nowrap ${
                    isPassing ? 'text-green-700' : 'text-red-700'
                  }`}
                >
                  {results.availableMarginKVA.toFixed(2)} kVA ·{' '}
                  {kvaToAmps(
                    results.availableMarginKVA,
                    project.service_voltage_numeric ?? 240,
                    project.service_phase === 'three' ? 'three' : 'single'
                  ).toFixed(1)}{' '}
                  A
                </td>
              </tr>
            </tbody>
          </table>
          </div>
        </div>

        {citationsFor(project).editionNote && (
          <p data-testid="edition-note" className="mt-3 text-xs text-gray-500">
            {citationsFor(project).editionNote}
          </p>
        )}

        {equipment.length > 0 && (
          <div className="mt-6 border-t border-gray-200 pt-6">
            <h4 className="font-semibold text-gray-900 mb-4 text-sm sm:text-base">
              Equipment Detail
            </h4>
            <div className="overflow-x-auto -mx-6 sm:mx-0">
              <table className="w-full text-xs sm:text-sm min-w-[400px]">
                <thead>
                  <tr className="border-b border-gray-200 bg-gray-50">
                    <th className="text-left p-2 font-semibold text-gray-700">
                      Equipment
                    </th>
                    <th className="text-right p-2 font-semibold text-gray-700 whitespace-nowrap">
                      kVA
                    </th>
                    <th className="text-center p-2 font-semibold text-gray-700 whitespace-nowrap">
                      Concurrent
                    </th>
                    {results.newLoadFactorsApplied && (
                      <>
                        <th className="text-right p-2 font-semibold text-gray-700 whitespace-nowrap">Factor</th>
                        <th className="text-right p-2 font-semibold text-gray-700 whitespace-nowrap">Counted</th>
                      </>
                    )}
                  </tr>
                </thead>
                <tbody className="divide-y divide-gray-100">
                  {results.equipmentCalculations.map((calc) => (
                    <tr key={calc.equipmentId}>
                      <td className="p-2 text-gray-900 break-words">
                        {calc.equipmentName}
                      </td>
                      <td className="p-2 text-right font-medium whitespace-nowrap">
                        {calc.kva.toFixed(2)}
                      </td>
                      <td className="p-2 text-center">
                        {calc.isConcurrent ? (
                          <span className="inline-block px-2 py-1 bg-blue-100 text-blue-800 rounded text-xs font-medium whitespace-nowrap">
                            Yes
                          </span>
                        ) : (
                          <span className="inline-block px-2 py-1 bg-gray-100 text-gray-600 rounded text-xs font-medium whitespace-nowrap">
                            No
                          </span>
                        )}
                      </td>
                      {results.newLoadFactorsApplied && (
                        <>
                          <td className="p-2 text-right whitespace-nowrap">{Math.round(calc.demandFactor * 100)}%</td>
                          <td className="p-2 text-right font-medium whitespace-nowrap">{calc.demandKVA.toFixed(2)}</td>
                        </>
                      )}
                    </tr>
                  ))}
                  <tr className="bg-gray-50 font-semibold">
                    <td className="p-2 text-gray-900">
                      Total All New Equipment
                    </td>
                    <td className="p-2 text-right whitespace-nowrap">
                      {results.totalAllNewLoadKVA.toFixed(2)}
                    </td>
                    <td className="p-2"></td>
                    {results.newLoadFactorsApplied && <><td className="p-2"></td><td className="p-2"></td></>}
                  </tr>
                  <tr className="bg-blue-50 font-semibold">
                    <td className="p-2 text-gray-900">
                      Total Concurrent New Equipment
                    </td>
                    <td className="p-2 text-right whitespace-nowrap">
                      {results.totalConcurrentNewLoadKVA.toFixed(2)}
                    </td>
                    <td className="p-2"></td>
                    {results.newLoadFactorsApplied && (
                      <>
                        <td className="p-2"></td>
                        <td className="p-2 text-right whitespace-nowrap">{results.totalConcurrentNewLoadDemandKVA.toFixed(2)}</td>
                      </>
                    )}
                  </tr>
                </tbody>
              </table>
            </div>
          </div>
        )}

        {results.removedCalculations.length > 0 && (
          <div data-testid="results-removed-equipment" className="mt-6 border-t border-gray-200 pt-6">
            <h4 className="font-semibold text-gray-900 mb-2 text-sm sm:text-base">
              Equipment Being Removed
            </h4>
            <p className="text-xs text-gray-600 mb-3">
              {results.removedLoadCreditApplied
                ? 'Credited at 40% of rating when it runs in the peak season (NEC 2026).'
                : 'Listed on the report, not credited: this code edition gives the measured method no subtraction for removed equipment.'}
            </p>
            <div className="overflow-x-auto -mx-6 sm:mx-0">
              <table className="w-full text-xs sm:text-sm min-w-[400px]">
                <thead>
                  <tr className="border-b border-gray-200 bg-gray-50">
                    <th className="text-left p-2 font-semibold text-gray-700">Equipment</th>
                    <th className="text-right p-2 font-semibold text-gray-700 whitespace-nowrap">kVA</th>
                    <th className="text-center p-2 font-semibold text-gray-700 whitespace-nowrap">Ran at peak</th>
                    <th className="text-right p-2 font-semibold text-gray-700 whitespace-nowrap">Credit</th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-gray-100">
                  {results.removedCalculations.map((r) => (
                    <tr key={r.equipmentId}>
                      <td className="p-2 text-gray-900 break-words">{r.equipmentName}</td>
                      <td className="p-2 text-right whitespace-nowrap">{r.kva.toFixed(2)}</td>
                      <td className="p-2 text-center">{r.isConcurrent ? 'Yes' : 'No'}</td>
                      <td className="p-2 text-right font-medium whitespace-nowrap">{r.creditKVA.toFixed(2)}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </div>
        )}
        </>
        )}

        {/* A failed calc is never a dead end (spec §5.6). The next move depends
            on what the other path can offer; load management is deliberately
            not suggested. */}
        {!isPassing && basis === 'traditional' && measuredResults?.adequacyResult === 'PASS' && (
          <div data-testid="fail-rescue" className="mt-6 flex items-start gap-3 rounded-lg border border-green-300 bg-green-50 p-4">
            <CheckCircle className="w-5 h-5 text-green-600 flex-shrink-0 mt-0.5" />
            <div className="text-sm text-green-900">
              <p className="font-semibold">Measured demand proves this service passes.</p>
              <p className="mt-1 leading-relaxed">
                The traditional calculation requires an upgrade, but the home's own measured
                data shows {measuredResults.adjustedDemandKVA.toFixed(2)} kVA against{' '}
                {measuredResults.serviceCapacityKVA.toFixed(2)} kVA of capacity under NEC {measuredCite}.
                Set Measured as the report basis above.
              </p>
            </div>
          </div>
        )}
        {!isPassing && basis === 'traditional' && !measuredReady && (
          <div data-testid="fail-try-measured" className="mt-6 flex items-start gap-3 rounded-lg border border-blue-200 bg-blue-50 p-4">
            <AlertCircle className="w-5 h-5 text-blue-600 flex-shrink-0 mt-0.5" />
            <div className="text-sm text-blue-900">
              <p className="font-semibold">Before quoting a service upgrade: try measured demand.</p>
              <p className="mt-1 leading-relaxed">
                Homes typically calculate about 40% lower under NEC {measuredCite}, which uses the
                home's own utility data instead of the code's estimate. Request the customer's
                interval data under Existing Load → Measured demand — this traditional result
                stays saved either way.
              </p>
              {onNavigate && (
                <button
                  onClick={() => onNavigate('ami')}
                  data-testid="btn-goto-measured"
                  className="mt-2 text-xs font-semibold text-blue-700 underline hover:text-blue-900"
                >
                  Open the Existing Load tab
                </button>
              )}
            </div>
          </div>
        )}
        {!isPassing && basis === 'measured' && traditionalResults?.adequacyResult === 'PASS' && (
          <div data-testid="fail-traditional-passes" className="mt-6 flex items-start gap-3 rounded-lg border border-blue-200 bg-blue-50 p-4">
            <AlertCircle className="w-5 h-5 text-blue-600 flex-shrink-0 mt-0.5" />
            <div className="text-sm text-blue-900">
              <p className="font-semibold">The traditional calculation passes for this home.</p>
              <p className="mt-1 leading-relaxed">
                Unusual but possible. Review both sets of inputs, and switch the report basis
                above if the traditional method is the right basis for this permit.
              </p>
            </div>
          </div>
        )}

        <div className="mt-6 p-4 bg-gray-50 border border-gray-300 rounded-md">
          <p className="text-sm text-gray-700">
            <strong>Important:</strong> This evaluation addresses service
            capacity only under NEC {basisCitation} and does not waive any other Code
            requirements including conductor sizing, overcurrent protection,
            branch circuits, or other applicable standards. Final approval
            remains subject to the Authority Having Jurisdiction.
          </p>
        </div>
      </div>

      {/* Report Modal */}
      {showReportValidationModal && reportValidation && (
        <CompletionValidationModal
          result={reportValidation}
          mode="report"
          onClose={() => setShowReportValidationModal(false)}
          onComplete={() => handleOpenReport()}
          onNavigate={(tab) => {
            setShowReportValidationModal(false);
            // 'report' and 'results' are in the modal's Tab union but are never
            // produced by FIELD_TO_TAB; the guard narrows to the editable tabs.
            if (tab === 'report' || tab === 'results') return;
            onNavigate?.(tab);
          }}
        />
      )}

      {showReportModal && (
        <div data-testid="report-modal" className="fixed inset-0 z-[100] flex flex-col bg-white">
          {/* Modal header */}
          <div className="no-print flex items-center justify-between px-4 sm:px-6 py-3 sm:py-4 bg-gray-900 border-b border-gray-700 flex-shrink-0">
            <h2 className="text-base sm:text-lg font-semibold text-white">
              Load Analysis Report
            </h2>
            <button
              onClick={handleCloseModal}
              data-testid="btn-report-close"
              className="p-2 text-gray-400 hover:text-white hover:bg-gray-700 rounded-md transition-colors"
              aria-label="Close report"
            >
              <X className="w-5 h-5" />
            </button>
          </div>

          {/* Report iframe */}
          <iframe
            ref={iframeRef}
            src={reportBlobUrl || undefined}
            className="report-print-content flex-1 w-full border-0"
            title="Load Analysis Report"
            sandbox="allow-same-origin allow-scripts allow-modals"
          />

          {/* Action bar */}
          <div data-testid="report-actions" className="no-print flex flex-wrap items-center gap-2 sm:gap-3 px-4 sm:px-6 py-3 sm:py-4 bg-gray-900 border-t border-gray-700 flex-shrink-0">
            <div data-testid="report-action-buttons" className="flex flex-wrap items-center gap-2 sm:gap-3">
            <button
              onClick={handlePrint}
              data-testid="btn-report-print"
              className="flex items-center gap-2 px-4 py-2 bg-blue-600 text-white rounded-md hover:bg-blue-700 transition-colors text-sm font-medium"
            >
              <Printer className="w-4 h-4" />
              Print
            </button>
            <button
              onClick={handleDownloadAndEmail}
              data-testid="btn-report-download"
              disabled={generatingPdf || !reportPdfBlob}
              className="flex items-center gap-2 px-4 py-2 bg-gray-600 text-white rounded-md hover:bg-gray-500 transition-colors text-sm font-medium disabled:opacity-50 disabled:cursor-not-allowed"
            >
              {generatingPdf ? (
                <div className="animate-spin rounded-full h-4 w-4 border-2 border-white border-t-transparent" />
              ) : (
                <Mail className="w-4 h-4" />
              )}
              Download &amp; Email
            </button>
            {canShare && (
              <div className="flex items-center gap-2">
                <button
                  onClick={handleShare}
              data-testid="btn-report-share"
                  disabled={generatingPdf || !reportPdfBlob}
                  className="flex items-center gap-2 px-4 py-2 bg-gray-600 text-white rounded-md hover:bg-gray-500 transition-colors text-sm font-medium disabled:opacity-50 disabled:cursor-not-allowed"
                >
                  <Share2 className="w-4 h-4" />
                  Share
                </button>
                {shareNotSupported && (
                  <span className="text-xs text-gray-400">
                    File sharing not supported on this device.
                  </span>
                )}
              </div>
            )}
            </div>
          </div>
        </div>
      )}

      {unlockToast && (
        <div className="fixed bottom-4 right-4 z-50 flex items-center gap-2 px-4 py-3 bg-green-600 text-white rounded-lg shadow-lg text-sm font-medium">
          <CheckCircle size={16} className="flex-shrink-0" />
          {unlockToast}
        </div>
      )}
    </div>
  );
}
